import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '@/auth/api';
import Loader from '@/components/Loader';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import {
  Users,
  Award,
  Search,
  Filter,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Sparkles,
  ArrowUpDown,
  Mail,
  Clock
} from 'lucide-react';
import SimplePagination from "@/components/SimplePagination";
import PageSizeSelector from "@/components/PageSizeSelector";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function ReferralStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [isAgentFilter, setIsAgentFilter] = useState('all'); // 'all', 'true', 'false'
  const [sortField, setSortField] = useState('successful_referrals');
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (isAgentFilter === 'true') {
        params.is_agent = true;
      } else if (isAgentFilter === 'false') {
        params.is_agent = false;
      }
      const resp = await api.get('/account/panel/referral/stats', { params });
      if (resp?.data) {
        setStats(resp.data);
      } else {
        setError('No data received from server.');
      }
    } catch (err) {
      console.error('Error fetching referral stats:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load referral stats.');
    } finally {
      setLoading(false);
    }
  }, [isAgentFilter]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Reset page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, isAgentFilter]);

  // Helper to mask email address to comply with PII security rules
//   const maskEmail = (email) => {
//     if (!email) return '';
//     const parts = email.split('@');
//     if (parts.length !== 2) return email;
//     const [name, domain] = parts;
//     if (name.length <= 2) {
//       return `${name[0]}***@${domain}`;
//     }
//     return `${name[0]}${'*'.repeat(name.length - 2)}${name[name.length - 1]}@${domain}`;
//   };

  // Safe KPI parsing
  const { global_stats = {}, user_stats = [], last_processed_at = {} } = stats || {};

  const totalCodes = global_stats.total_refer_codes || 0;
  const totalReferred = global_stats.total_referred || 0;
  const successfulReferrals = global_stats.successful_referrals || 0;
  const failedReferrals = global_stats.failed_referrals || 0;
  const totalWSC = global_stats.total_wsc_points || 0;
  const totalWUC = global_stats.total_wuc_points || 0;

  const conversionRate = totalReferred > 0 ? ((successfulReferrals / totalReferred) * 100).toFixed(1) : '0.0';
  const pendingReferrals = Math.max(0, totalReferred - (successfulReferrals + failedReferrals));

  // 1. Filtered Users
  const filteredUsers = useMemo(() => {
    return user_stats.filter((user) => {
      // Search logic
      const query = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !query ||
        user.user_name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        String(user.user_id).includes(query);

      // Access type filter
      let matchesType = true;
      if (isAgentFilter === 'true') {
        matchesType = user.access_type === 'Agent' || user.is_agent === true;
      } else if (isAgentFilter === 'false') {
        matchesType = user.access_type === 'Respondent' && !user.is_agent;
      }

      return matchesSearch && matchesType;
    });
  }, [user_stats, searchQuery, isAgentFilter]);

  // 2. Sorted Users
  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers];
    if (sortField) {
      sorted.sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];

        if (typeof valA === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }

        if (valA === undefined || valA === null) valA = 0;
        if (valB === undefined || valB === null) valB = 0;

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [filteredUsers, sortField, sortDirection]);

  // 3. Paginated Users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedUsers.slice(startIndex, startIndex + pageSize);
  }, [sortedUsers, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedUsers.length / pageSize) || 1;

  // Sorting Handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc'); // default sort is descending (e.g. for numbers)
    }
  };

  // Top 5 Referrers chart config
  const topPerformers = useMemo(() => {
    return [...user_stats]
      .sort((a, b) => (b.successful_referrals || 0) - (a.successful_referrals || 0))
      .slice(0, 5);
  }, [user_stats]);

  const doughnutData = {
    labels: ['Successful', 'Failed', 'Pending'],
    datasets: [
      {
        data: [successfulReferrals, failedReferrals, pendingReferrals],
        backgroundColor: [
          '#67a97b', // green_Main
          '#ef4444', // red
          '#f59e0b'  // yellow/orange
        ],
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)'
      }
    ]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: 'currentColor',
          boxWidth: 12,
          font: { family: 'Inter, sans-serif', size: 12 }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const val = context.raw;
            const pct = totalReferred > 0 ? ((val / totalReferred) * 100).toFixed(1) : 0;
            return ` ${context.label}: ${val} (${pct}%)`;
          }
        }
      }
    }
  };

  const barData = {
    labels: topPerformers.map((u) => u.user_name || `ID ${u.user_id}`),
    datasets: [
      {
        label: 'Successful Referrals',
        data: topPerformers.map((u) => u.successful_referrals || 0),
        backgroundColor: 'rgba(35, 98, 159, 0.85)', // var(--mainColLight)
        borderColor: 'rgba(35, 98, 159, 1)',
        borderWidth: 1,
        borderRadius: 4
      }
    ]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => ` Successful: ${context.raw}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(156, 163, 175, 0.1)' },
        ticks: { precision: 0 }
      },
      x: {
        grid: { display: false }
      }
    }
  };

  if (loading) {
    return (
      <div className="w-full pt-28 flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-2xl mx-auto mt-28 p-6 bg-red-50 border border-red-200 rounded-2xl text-center">
        <p className="text-red-700 font-semibold mb-2">Error Loading Referral Stats</p>
        <p className="text-sm text-red-500 mb-4">{error}</p>
        <button
          onClick={fetchStats}
          className="activeBtn px-4 py-2 text-white rounded-lg text-sm hover:opacity-90 transition flex items-center justify-center mx-auto gap-2"
        >
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto px-4 py-6 flex flex-col items-center">
      
      {/* Dashboard Top Header */}
      <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900">Referral Analytics</h2>
          <p className="text-sm text-gray-500">Real-time overview of referral code performance and leaderboard stats.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 self-start sm:self-center">
          {last_processed_at?.last_processed_at && (
            <div className="flex items-center gap-1.5 bg-gray-55 border border-gray-150 px-3 py-1.5 rounded-xl text-xs text-gray-500 shadow-sm">
              <Clock size={12} />
              <span>Last Processed: <strong>{last_processed_at.last_processed_at}</strong></span>
            </div>
          )}

          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-1.5 text-xs text-gray-700 shadow-sm">
            <Filter size={12} className="text-gray-400" />
            <span className="font-bold text-gray-500 uppercase tracking-wider">Is Agent:</span>
            <select
              value={isAgentFilter}
              onChange={(e) => setIsAgentFilter(e.target.value)}
              className="bg-transparent border-none text-xs text-gray-700 focus:outline-none font-semibold cursor-pointer"
            >
              <option value="all">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>
      </div>

      {/* KPI Global Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 w-full">
        {/* Codes Generated */}
        <div className="bg-white rounded-2xl p-5 border border-gray-150 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex justify-between items-center mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Generated Codes</span>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <Award size={18} />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900">{totalCodes}</div>
          <div className="text-xs text-gray-500 mt-2">Active referral source codes</div>
        </div>

        {/* Total Referred */}
        <div className="bg-white rounded-2xl p-5 border border-gray-150 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex justify-between items-center mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Total Referred</span>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
              <Users size={18} />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900">{totalReferred}</div>
          <div className="text-xs text-purple-600 font-semibold mt-2 flex items-center gap-1">
            <TrendingUp size={12} />
            {conversionRate}% Conversion Rate
          </div>
        </div>

        {/* Conversions Status */}
        <div className="bg-white rounded-2xl p-5 border border-gray-150 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex justify-between items-center mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Conversion Status</span>
            <div className="p-2 bg-green-50 text-green-600 rounded-lg">
              <CheckCircle size={18} />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900">
            <span className="text-green-600">{successfulReferrals}</span>
            <span className="text-gray-300 mx-1">/</span>
            <span className="text-red-500">{failedReferrals}</span>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Successful / Failed registrations
          </div>
        </div>

        {/* Total Points */}
        <div className="bg-white rounded-2xl p-5 border border-gray-150 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex justify-between items-center mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Distributed Points</span>
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <Sparkles size={18} />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {totalWSC + totalWUC} <span className="text-xs font-normal text-gray-400">pts</span>
          </div>
          <div className="text-xs text-gray-500 mt-2 flex justify-between">
            <span>WSC: <strong>{totalWSC}</strong></span>
            <span>WUC: <strong>{totalWUC}</strong></span>
          </div>
        </div>
      </div>

      {/* Visual Analytics Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8 w-full">
        {/* Conversion breakdown Doughnut */}
        <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm lg:col-span-4 flex flex-col items-center">
          <h3 className="text-sm font-bold text-gray-700 self-start mb-4">Conversion Breakdown</h3>
          <div className="relative w-full h-64 flex justify-center items-center">
            {totalReferred > 0 ? (
              <Doughnut data={doughnutData} options={doughnutOptions} />
            ) : (
              <div className="text-gray-400 text-sm flex flex-col items-center gap-1">
                <AlertCircle size={20} className="text-gray-300" />
                No referred users to display
              </div>
            )}
          </div>
        </div>

        {/* Top 5 performes Bar chart */}
        <div className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm lg:col-span-8 flex flex-col">
          <h3 className="text-sm font-bold text-gray-700 mb-4">Top 5 Referrers (Successful Conversions)</h3>
          <div className="relative w-full h-64 flex-1">
            {topPerformers.length > 0 ? (
              <Bar data={barData} options={barOptions} />
            ) : (
              <div className="flex justify-center items-center h-full text-gray-400 text-sm">
                No stats data to show
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Leaderboard search row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 w-full bg-white p-4 rounded-xl border border-gray-150 shadow-sm">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            <Search size={16} />
          </div>
          <input
            type="text"
            placeholder="Search name, email, user ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl bg-gray-50/50 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
          />
        </div>

        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-semibold"
          >
            Clear Search
          </button>
        )}
      </div>

      {/* User Stats Table */}
      <div className="bg-white rounded-2xl border border-gray-150 shadow-sm overflow-hidden w-full">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                <th
                  onClick={() => handleSort('user_id')}
                  className="py-3.5 px-4 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-1 select-none">
                    User ID
                    <ArrowUpDown size={12} className={sortField === 'user_id' ? 'text-blue-600' : 'text-gray-300'} />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('user_name')}
                  className="py-3.5 px-4 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-1 select-none">
                    User Info
                    <ArrowUpDown size={12} className={sortField === 'user_name' ? 'text-blue-600' : 'text-gray-300'} />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('access_type')}
                  className="py-3.5 px-4 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-1 select-none">
                    Access Type
                    <ArrowUpDown size={12} className={sortField === 'access_type' ? 'text-blue-600' : 'text-gray-300'} />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('method')}
                  className="py-3.5 px-4 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-1 select-none">
                    Method
                    <ArrowUpDown size={12} className={sortField === 'method' ? 'text-blue-600' : 'text-gray-300'} />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('total_refer_codes')}
                  className="py-3.5 px-4 cursor-pointer hover:bg-gray-100 text-center transition-colors"
                >
                  <div className="flex items-center justify-center gap-1 select-none">
                    Codes
                    <ArrowUpDown size={12} className={sortField === 'total_refer_codes' ? 'text-blue-600' : 'text-gray-300'} />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('total_referred')}
                  className="py-3.5 px-4 cursor-pointer hover:bg-gray-100 text-center transition-colors"
                >
                  <div className="flex items-center justify-center gap-1 select-none">
                    Referred
                    <ArrowUpDown size={12} className={sortField === 'total_referred' ? 'text-blue-600' : 'text-gray-300'} />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('successful_referrals')}
                  className="py-3.5 px-4 cursor-pointer hover:bg-gray-100 text-center transition-colors"
                >
                  <div className="flex items-center justify-center gap-1 select-none">
                    Success
                    <ArrowUpDown size={12} className={sortField === 'successful_referrals' ? 'text-blue-600' : 'text-gray-300'} />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('failed_referrals')}
                  className="py-3.5 px-4 cursor-pointer hover:bg-gray-100 text-center transition-colors"
                >
                  <div className="flex items-center justify-center gap-1 select-none">
                    Failed
                    <ArrowUpDown size={12} className={sortField === 'failed_referrals' ? 'text-blue-600' : 'text-gray-300'} />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('total_wsc_points')}
                  className="py-3.5 px-4 cursor-pointer hover:bg-gray-100 text-right transition-colors"
                >
                  <div className="flex items-center justify-end gap-1 select-none">
                    WSC
                    <ArrowUpDown size={12} className={sortField === 'total_wsc_points' ? 'text-blue-600' : 'text-gray-300'} />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('total_wuc_points')}
                  className="py-3.5 px-4 cursor-pointer hover:bg-gray-100 text-right transition-colors"
                >
                  <div className="flex items-center justify-end gap-1 select-none">
                    WUC
                    <ArrowUpDown size={12} className={sortField === 'total_wuc_points' ? 'text-blue-600' : 'text-gray-300'} />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((user, idx) => (
                  <tr key={`${user.user_id}-${idx}`} className="hover:bg-gray-55/40 hover:bg-gray-50/50 transition-colors">
                    <td className="py-3.5 px-4 text-xs font-semibold text-gray-500">#{user.user_id}</td>
                    <td className="py-3.5 px-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900">{user.user_name}</span>
                        <span className="text-xs text-gray-550 text-gray-500 font-mono flex items-center gap-1">
                          <Mail size={10} />
                          {(user.email)}
                        </span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.access_type === 'Agent' || user.is_agent
                            ? 'bg-blue-50 text-blue-700 border border-blue-100'
                            : 'bg-gray-100 text-gray-750 text-gray-700 border border-gray-200'
                        }`}
                      >
                        {user.access_type || (user.is_agent ? 'Agent' : 'Respondent')}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-xs font-semibold text-gray-500">{user.method || "-"}</td>
                    <td className="py-3.5 px-4 text-center font-medium">{user.total_refer_codes ?? 0}</td>
                    <td className="py-3.5 px-4 text-center font-bold text-gray-900">{user.total_referred ?? 0}</td>
                    <td className="py-3.5 px-4 text-center font-bold text-green-600">{user.successful_referrals ?? 0}</td>
                    <td className="py-3.5 px-4 text-center font-bold text-red-500">{user.failed_referrals ?? 0}</td>
                    <td className="py-3.5 px-4 text-right font-semibold text-indigo-600">{user.total_wsc_points ?? 0}</td>
                    <td className="py-3.5 px-4 text-right font-semibold text-indigo-600">{user.total_wuc_points ?? 0}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-12 text-gray-400">
                    <div className="flex flex-col items-center gap-2">
                      <AlertCircle size={24} className="text-gray-300" />
                      <p className="font-semibold text-gray-700">No matching stats found</p>
                      <p className="text-xs text-gray-500">Try adjusting your filters or search query.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Table Pagination Footer */}
        {sortedUsers.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50/50 p-4 border-t border-gray-100">
            <span className="text-xs text-gray-500">
              Showing <strong>{(currentPage - 1) * pageSize + 1}</strong> to{' '}
              <strong>{Math.min(currentPage * pageSize, sortedUsers.length)}</strong> of{' '}
              <strong>{sortedUsers.length}</strong> users
            </span>
            <div className="flex items-center gap-4">
              <PageSizeSelector pageSize={pageSize} setPageSize={(size) => { setPageSize(size); setCurrentPage(1); }} />
              <SimplePagination currentPage={currentPage} setCurrentPage={setCurrentPage} totalPages={totalPages} />
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default ReferralStats;